/* eslint-disable i18next/no-literal-string */
import mixpanel, { Mixpanel as MixpanelIntf, RequestOptions } from 'mixpanel-browser'

// Constants
import {
  CONFIG_MIXPANEL_PROJECT_ID,
  CONFIG_MIXPANEL_PROXY_API_ENDPOINT,
  CONFIG_MIXPANEL_PROXY_PROJECT_ID,
} from '@src/config/env'
import { getIsCountryCompliant } from '@src/modules/global-state/locale-config.browser'
import { getUserAgnosticGlobalFeatureGatesFromMemory } from '@src/modules/global-state/user-agnostic-feature-gates.browser'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { getUserIp } from '@src/quince-site-common/src/services/ipService/ipService'
import { checkApiBlocked } from '@src/quince-site-common/src/utils/checkApiBlocked'
import { canTrack } from '@src/quince-site-common/src/utils/checkDoNotTrack'
import { ANALYTIC_COOKIES } from '@src/quince-site-common/src/utils/constants'
import { captureException, captureFatalException } from '@src/quince-site-common/src/utils/sentryUtil'
import { handleSgtmApi } from '@src/telemetry/handleSgtmApi'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
// Utils
import { trackEvent } from '@src/telemetry/track'

import generateAlphaNumericId from '../../../quince-site-common/src/utils/generateAlphaNumericId'
import getCookie from '../../../quince-site-common/src/utils/getCookie'
import setCookie from '../../../quince-site-common/src/utils/setCookie'
// Helpers
import { validateLbUserId, validateSessionId } from '../provider-utils/internal-analytics-utils'

import { getCommonProperties, getTransformedProperties, isBotAgent } from './mixpanel.helper'

// Services

export class Mixpanel {
  private isInitialised = false
  private mpQueue: Record<string, any>[] = []

  /**
   * Kill-switch for every method that touches the `mixpanel-browser` browser SDK
   * (`init`, `identify`, `reset`, `track`). Gated by the `ssr_mixpanel_sdk_enabled`
   * Statsig gate — `true` for US/CA, `false` for UK (see FEATURE_GATE_REGISTRY).
   *
   * Returns true ONLY when the gate is explicitly `false`. Treats `undefined`
   * (gate not yet hydrated from Statsig) as "proceed" to match the registry
   * default of `true`. The bootstrap call site in
   * `dynamic-client-root-layout.tsx` already strict-truthy gates `init()`, so
   * any post-init call running before Statsig finishes loading is still safe.
   *
   * NOTE: `trackWithSgtm` is intentionally NOT gated by this — SGTM routes
   * through `handleSgtmApi` and does not load the browser SDK. SGTM consent
   * gating lives in `InternalAnalytics` + TrustArc.
   */
  private isSdkDisabled(): boolean {
    // Short-circuit ONLY on explicit false. Treat undefined (gate not yet
    // hydrated from Statsig, partial mock in tests, or function not mocked at
    // all) as "proceed" to match the FEATURE_GATE_REGISTRY default of true.
    // The optional chain (`?.`) is defensive — the TS signature says the
    // function returns a non-null object, but test environments may inject
    // undefined when the mock isn't configured.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare
    return getUserAgnosticGlobalFeatureGatesFromMemory()?.ssr_mixpanel_sdk_enabled === false
  }

  init() {
    if (this.isSdkDisabled()) return
    try {
      const isTrackingEnabled = canTrack()
      if (!isTrackingEnabled) {
        trackEvent(TELEMETRY_EVENT_NAMES.DO_NOT_TRACK, {
          context: {
            trackingEnabled: false,
          },
        })
      }

      void checkApiBlocked('https://api-js.mixpanel.com/track/?verbose=1&ip=1&_=1721377158412').then(
        ({ blocked, errorType }) => {
          if (blocked) {
            trackEvent(TELEMETRY_EVENT_NAMES.API_RESPONSE, {
              apiName: 'mixpanelTrack',
              status: 'blocked',
              errorType,
            })
          }
        },
      )

      if (CONFIG_MIXPANEL_PROJECT_ID && CONFIG_MIXPANEL_PROXY_PROJECT_ID && CONFIG_MIXPANEL_PROXY_API_ENDPOINT) {
        void getUserIp()

        mixpanel.init(CONFIG_MIXPANEL_PROJECT_ID, {
          persistence: 'localStorage',
          debug: true,
          batch_requests: false,
        })
        mixpanel.init(
          CONFIG_MIXPANEL_PROXY_PROJECT_ID,
          {
            persistence: 'localStorage',
            debug: true,
            loaded: this.initialisationCheck,
            api_host: CONFIG_MIXPANEL_PROXY_API_ENDPOINT,
            batch_requests: false,
          },
          'proxy_instance',
        )
      }
    } catch (error) {
      captureFatalException(`Failed to initialize mixpanel : ${error}`, {
        module: MODULES.MIXPANEL_ANALYTICS,
        feature: FEATURES.MIXPANEL_ANALYTICS_API,
      })
    }
  }

  identify({ email, isTentativeEmail = true }: { email: string; isTentativeEmail?: boolean }) {
    if (this.isSdkDisabled()) return
    try {
      const useCustomerId = getUserAgnosticGlobalFeatureGatesFromMemory()?.ssr_mixpanel_identify_by_customer_id

      // UK (countryCompliant): never send the raw email to Mixpanel. Identify by the
      // pseudonymous customerId / device id instead (the same identifier SGTM uses as
      // $user_id post-consent), so no PII email reaches Mixpanel — cookie OR server.
      // US/CA: condition reduces to `useCustomerId`, behaviour unchanged.
      if (useCustomerId || getIsCountryCompliant()) {
        const finalCustomerId =
          getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_ID) ??
          // eslint-disable-next-line @quince/no-direct-localstorage
          localStorage.getItem(ANALYTIC_COOKIES.LB_CUSTOMER_ID) ??
          this.getMixpanelUserDeviceId()

        if (finalCustomerId) {
          mixpanel.identify(finalCustomerId)
        }
      } else {
        if (email) {
          if (isTentativeEmail) {
            this.setMixpanelTentativeEmail(email)
          } else {
            mixpanel.identify(email)
          }
        }
      }
    } catch (error) {
      captureException(`Failed to identify : ${error}`, {
        module: MODULES.MIXPANEL_ANALYTICS,
        feature: FEATURES.MIXPANEL_ANALYTICS_API,
      })
    }
  }

  reset() {
    if (this.isSdkDisabled()) return
    try {
      mixpanel.reset()
      this.register()
    } catch (err) {
      captureFatalException(`Failed to reset user after logout : ${err}`, {
        module: MODULES.MIXPANEL_ANALYTICS,
        feature: FEATURES.MIXPANEL_ANALYTICS_API,
      })
    }
  }

  track(properties: Record<string, unknown>) {
    if (this.isSdkDisabled()) return
    try {
      if (!isBotAgent()) {
        if (this.isInitialised) {
          this.fetchDependencies(properties)
          this.emptyMpQueue()
          this.sendEvent(properties)
        } else {
          this.pushToMpQueue(properties)
        }
      }
    } catch (err) {
      captureFatalException(`Failed to track event with mixpanel : ${err}`, {
        module: MODULES.MIXPANEL_ANALYTICS,
        feature: FEATURES.MIXPANEL_ANALYTICS_API,
      })
    }
  }

  trackWithSgtm(properties: Record<string, unknown>) {
    try {
      if (!isBotAgent()) {
        const transformedProperties = getTransformedProperties(properties)
        const commonProperties = getCommonProperties(properties)
        if (transformedProperties) {
          const serverProperties = {
            ...transformedProperties,
            ...commonProperties.sgtmCommonProperties,
          }
          handleSgtmApi(serverProperties, 'mixpanel_sgtm_api', 'mixpanel_properties')
        }
      }
    } catch (err) {
      captureFatalException(`Failed to track event with mixpanel through sgtm: ${err}`, {
        module: MODULES.MIXPANEL_ANALYTICS,
        feature: FEATURES.MIXPANEL_ANALYTICS_API,
      })
    }
  }

  private getShouldSendToProxy = (event_name: string) => {
    return event_name === 'Page Viewed'
  }

  // Callback that runs after mixpanel is loaded and initialised
  private initialisationCheck = () => {
    console.info('Mixpanel event stream : Initialisation is done')
    this.fetchDependencies()
    this.isInitialised = true
    this.emptyMpQueue()
    console.info('Mixpanel event stream : Initialisation check is done')
  }

  // Generates mixpanel user device id and links it to mixpanel
  private register() {
    const uid = this.getUniqueId()
    console.info('Mixpanel event stream : setting mixpanel user device id to ', uid)
    this.setMixpanelUserDeviceId(uid)
  }

  /**
   * Generates and persists a fresh Mixpanel device ID for UK users on first
   * consent grant. Called from inject-consent-scripts after advertising consent
   * is approved — ensures pre-consent (anonymous) events cannot be linked to
   * post-consent events via a shared device ID.
   *
   * Safe to call even if SDK is disabled (UK) — only writes the cookie, does
   * not touch mixpanel-browser SDK.
   */
  /**
   * Generates a device ID on first UK page load if one doesn't exist yet.
   * Ensures $device_id is present in all SGTM events including pre-consent ones.
   * On consent grant, rotateDeviceId() replaces it with a fresh UUID so
   * pre-consent and post-consent events cannot be linked.
   */
  initDeviceId() {
    if (!this.getMixpanelUserDeviceId()) {
      const uid = this.getUniqueId()
      this.setMixpanelUserDeviceId(uid)
    }
  }

  rotateDeviceId() {
    const uid = this.getUniqueId()
    this.setMixpanelUserDeviceId(uid)
  }

  private setMixpanelTentativeEmail = (email: string) => {
    // UK (countryCompliant): never persist raw email to a cookie, even with the
    // browser SDK enabled post-consent — keeps UK consistent with the LB_CUSTOMER_*
    // email-cookie gates. US/CA unchanged.
    if (getIsCountryCompliant()) return
    setCookie(ANALYTIC_COOKIES.MIXPANEL_TENTATIVE_EMAIL, email)
  }

  private getMixpanelUserDeviceId = (): string | undefined => {
    return getCookie(ANALYTIC_COOKIES.MIXPANEL_USER_DEVICE_ID)
  }

  private setMixpanelUserDeviceId = (userDeviceId: string) => {
    setCookie(ANALYTIC_COOKIES.MIXPANEL_USER_DEVICE_ID, userDeviceId)
  }

  private getUniqueId() {
    let id
    if (crypto.randomUUID) {
      id = crypto.randomUUID()
    } else {
      id = generateAlphaNumericId(36)
    }
    return id
  }

  private fetchDependencies(_properties?: Record<string, unknown>) {
    console.info('Mixpanel event stream : Fetching dependencies')
    validateLbUserId()
    validateSessionId()

    if (!this.getMixpanelUserDeviceId()) {
      console.info('Mixpanel event stream : mixpanel user device id is not present, creating now')
      this.register()
    }
  }

  private pushToMpQueue(event: Record<string, unknown>) {
    console.info('Mixpanel event stream : initialisation is pending, pushing to queue ', event)
    this.mpQueue.push(event)
  }

  private emptyMpQueue() {
    const currentQueue = [...this.mpQueue]
    console.info('Mixpanel event stream : emptying queue : ', currentQueue)
    this.mpQueue = []
    currentQueue.forEach(event => {
      this.sendEvent(event)
    })
  }

  private getShouldSendImmediately = (properties: unknown) => {
    if (properties.entity_id === 'start_checkout') {
      return true
    }
    return false
  }

  private sendEvent(properties: Record<string, unknown>) {
    console.info('Mixpanel event stream : Sending event : ', properties)
    const transformedProperties: Record<string, unknown> | null = getTransformedProperties(properties)
    const commonProperties = getCommonProperties(properties)
    if (transformedProperties) {
      const clientProperties = {
        ...transformedProperties,
        ...commonProperties.clientCommonProperties,
      }

      const shouldSendImmediately = this.getShouldSendImmediately(properties)
      const options: RequestOptions = {
        transport: shouldSendImmediately ? 'sendBeacon' : 'xhr',
        send_immediately: shouldSendImmediately,
      }

      type OverridedMixpanelIntf = MixpanelIntf & {
        proxy_instance: MixpanelIntf
      }

      const mixpanelCasted = mixpanel as unknown as OverridedMixpanelIntf
      mixpanelCasted.track(transformedProperties.event_name as string, clientProperties, options)

      const shouldSendToProxy = this.getShouldSendToProxy(transformedProperties.event_name as string)
      if (shouldSendToProxy) {
        mixpanelCasted.proxy_instance.track(transformedProperties.event_name as string, clientProperties, options)
      }
    }
  }
}

const mixpanelCl = new Mixpanel()

export default mixpanelCl

/* eslint-enable i18next/no-literal-string */
